import {useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {setIsHeadlessMode} from 'src/redux/slices/interfaceSlice';

const STORAGE_KEY = 'PROACT_HEADLESS_MODE';

/**
 * Hook to manage headless mode state.
 *
 * Headless mode hides navigation chrome (sidebar, back buttons, breadcrumbs)
 * for embedded/linked access to specific pages.
 *
 * Triggered via URL parameter: ?headless=true
 * Disabled via URL parameter: ?headless=false
 * Persists for the browser window/tab session via sessionStorage.
 */
export const useHeadlessMode = (): boolean => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const isHeadlessMode = useAppSelector(state => state.interface.isHeadlessMode);

  useEffect(() => {
    const headlessParam = searchParams.get('headless');

    if (headlessParam === 'true') {
      sessionStorage.setItem(STORAGE_KEY, 'true');
      if (!isHeadlessMode) {
        dispatch(setIsHeadlessMode(true));
      }
    } else if (headlessParam === 'false') {
      sessionStorage.removeItem(STORAGE_KEY);
      if (isHeadlessMode) {
        dispatch(setIsHeadlessMode(false));
      }
    } else if (!isHeadlessMode) {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored === 'true') {
        dispatch(setIsHeadlessMode(true));
      }
    }
  }, [searchParams, dispatch, isHeadlessMode]);

  return isHeadlessMode;
};
